import {
	createReducer,
	PayloadAction,
	ActionReducerMapBuilder,
} from '@reduxjs/toolkit';
import {
	getAllAssetsSchemaResolved,
	getAssetSchemaBySystemTypeResolved,
	getAssetSchemaResolved,
} from '../actions/asset.actions';
import { AssetSchemaRaw, SystemTypeRaw } from '@innovyze/lib_asset_schema';
import { AssetSchemaStoreState } from '../types';
import {
	getAllSystemsSchemaResolved,
	getSystemSchemaResolved,
} from '../actions';

/**
 * Initial state for the asset schema store.
 */
const initialState: AssetSchemaStoreState = {
	assetTypes: {},
	systemTypes: {},
};

/**
 * Reducer for handling asset schema actions.
 * @param builder - The action reducer map builder.
 */
const assetSchemaReducer = (
	builder: ActionReducerMapBuilder<AssetSchemaStoreState>,
) => {
	builder.addCase(
		getAssetSchemaResolved.toString(),
		/**
		 * Handles the resolution of a single asset schema.
		 * @param state - The current state.
		 * @param action - The action containing the payload.
		 */
		(state, action: PayloadAction<[SystemTypeRaw, AssetSchemaRaw]>) => {
			const [rawSystem, rawAsset] = action.payload;

			return {
				...state,
				systemTypes: {
					...state.systemTypes,
					[rawSystem.SystemType]: rawSystem,
				},
				assetTypes: {
					...state.assetTypes,
					[rawAsset.AssetType]: rawAsset,
				},
			};
		},
	);
	builder.addCase(
		getAssetSchemaBySystemTypeResolved.toString(),
		/**
		 * Handles the resolution of multiple asset schemas by system type.
		 * @param state - The current state.
		 * @param action - The action containing the payload.
		 */
		(state, action: PayloadAction<[SystemTypeRaw, AssetSchemaRaw][]>) => {
			const init: {
				systemTypes: { [systemType: string]: SystemTypeRaw };
				assetTypes: { [assetType: string]: AssetSchemaRaw };
			} = { systemTypes: {}, assetTypes: {} };

			const newState = action.payload.reduce((acc, schema) => {
				const [rawSystem, rawAsset] = schema;
				acc.systemTypes[rawSystem.SystemType] = rawSystem;
				acc.assetTypes[rawAsset.AssetType] = rawAsset;
				return acc;
			}, init);

			return {
				...state,
				systemTypes: {
					...state.systemTypes,
					...newState.systemTypes,
				},
				assetTypes: {
					...state.assetTypes,
					...newState.assetTypes,
				},
			};
		},
	);
	builder.addCase(
		getAllAssetsSchemaResolved.toString(),
		/**
		 * Handles the resolution of all asset schemas.
		 * @param state - The current state.
		 * @param action - The action containing the payload.
		 */
		(state, action: PayloadAction<[SystemTypeRaw, AssetSchemaRaw][]>) => {
			const init: {
				systemTypes: { [systemType: string]: SystemTypeRaw };
				assetTypes: { [assetType: string]: AssetSchemaRaw };
			} = { systemTypes: {}, assetTypes: {} };

			const newState = action.payload.reduce((acc, schema) => {
				const [rawSystem, rawAsset] = schema;
				acc.systemTypes[rawSystem.SystemType] = rawSystem;
				acc.assetTypes[rawAsset.AssetType] = rawAsset;
				return acc;
			}, init);

			return {
				...state,
				systemTypes: {
					...state.systemTypes,
					...newState.systemTypes,
				},
				assetTypes: {
					...state.assetTypes,
					...newState.assetTypes,
				},
			};
		},
	);
	builder.addCase(
		getSystemSchemaResolved.toString(),
		/**
		 * Handles the resolution of a single system schema.
		 * @param state - The current state.
		 * @param action - The action containing the payload.
		 */
		(state, action: PayloadAction<SystemTypeRaw>) => {
			const rawSystem = action.payload;

			return {
				...state,
				systemTypes: {
					...state.systemTypes,
					[rawSystem.SystemType]: rawSystem,
				},
			};
		},
	);
	builder.addCase(
		getAllSystemsSchemaResolved.toString(),
		/**
		 * Handles the resolution of all system schemas.
		 * @param state - The current state.
		 * @param action - The action containing the payload.
		 */
		(state, action: PayloadAction<SystemTypeRaw[]>) => {
			const init: {
				systemTypes: { [systemType: string]: SystemTypeRaw };
			} = { systemTypes: {} };

			const newState = action.payload.reduce((acc, rawSystem) => {
				acc.systemTypes[rawSystem.SystemType] = rawSystem;
				return acc;
			}, init);

			return {
				...state,
				systemTypes: {
					...state.systemTypes,
					...newState.systemTypes,
				},
			};
		},
	);
};

/**
 * Root reducer for the asset schema store.
 */
export const assetSchemaRootReducer = {
	assetSchema: createReducer(initialState, assetSchemaReducer),
};
